import { useState, type FormEvent } from 'react';
import { setupVault, type AppSession } from '@/services/appSession';
import { useSession } from '../state/session';
import { PassphraseHint, passphraseProblem } from '../components/PassphraseHint';

/**
 * 첫 기기 설정 (로그인 화면 > 처음 쓰는 경우에서만): 비밀번호 확정 → 복구 키 1회 표시 → 보관 확인 (결정 D3).
 */
export function SetupScreen({
  initialPass = '',
  onBack,
  onDone,
}: {
  initialPass?: string;
  onBack: () => void;
  onDone?: (session: AppSession) => void;
}) {
  const setSession = useSession((s) => s.setSession);
  const [pass, setPass] = useState(initialPass);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ session: AppSession; recoveryKey: string } | null>(null);
  const [saved, setSaved] = useState(false);
  const [retyped, setRetyped] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const problem = passphraseProblem(pass) ?? (pass !== confirm ? '두 입력이 서로 다릅니다.' : null);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      setPending(await setupVault(pass));
    } catch {
      setError('설정에 실패했습니다. 다시 시도해 주세요. (저장된 내용은 없습니다)');
    } finally {
      setBusy(false);
    }
  }

  if (pending) {
    const norm = (s: string) => s.toUpperCase().replace(/[\s-]/g, '');
    const match = norm(retyped) === norm(pending.recoveryKey);
    return (
      <main className="narrow">
        <h1>복구 키를 보관하세요</h1>
        <p>
          비밀번호를 잊었을 때 자료를 열 수 있는 <strong>유일한 방법</strong>입니다. 이 화면을 닫으면 다시 볼 수
          없습니다. 종이에 적거나 안전한 곳에 따로 보관하세요.
        </p>
        <p className="recovery-key" data-testid="recovery-key">
          {pending.recoveryKey.split('-').map((g, i, all) => (
            <span key={i}>
              {g}
              {i < all.length - 1 ? '-' : ''}
            </span>
          ))}
        </p>
        <p className="warn">비밀번호와 복구 키를 모두 잃으면 자료는 누구도 복구할 수 없습니다.</p>
        <label className="check">
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> 복구 키를 안전한 곳에
          적어 두었습니다
        </label>
        <label>
          확인을 위해 복구 키를 다시 입력하세요
          <input value={retyped} onChange={(e) => setRetyped(e.target.value)} autoComplete="off" spellCheck={false} />
        </label>
        <button
          type="button"
          className="primary"
          disabled={!saved || !match}
          onClick={() => (onDone ? onDone(pending.session) : setSession(pending.session))}
        >
          시작하기
        </button>
      </main>
    );
  }

  return (
    <main className="narrow">
      <h1>이 기기에서 새로 시작</h1>
      <p className="muted">
        모든 상담 기록은 이 기기에서 암호화됩니다. 로그인 비밀번호가 곧 암호화 키이며 어디에도 저장되지 않습니다.
      </p>
      <form onSubmit={onSubmit}>
        <label>
          비밀번호
          <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} autoComplete="new-password" />
        </label>
        <PassphraseHint value={pass} />
        <label>
          비밀번호 다시 입력
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </label>
        {error && (
          <p role="alert" className="warn">
            {error}
          </p>
        )}
        <button type="submit" className="primary" disabled={busy}>
          {busy ? '암호화 키 만드는 중…' : '설정'}
        </button>
      </form>
      <button type="button" className="link" onClick={onBack}>
        돌아가기
      </button>
    </main>
  );
}
